import {
    useEffect,
    useState,
    useRef,
    type FC
} from "react";
import {
    type NativeSyntheticEvent,
    type NativeScrollEvent,
    type LayoutChangeEvent,
    ScrollView,
    Platform
} from "react-native";
import type IMessageScrollerViewportProps from "./type";
import type {
    MessageScrollerViewportKeyboardEventType,
    MessageScrollerViewportWheelEventType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useMessageScrollerRootContext,
    useMessageScrollerContext
} from "../../context";
import {
    warnOnce
} from "../../../../utils/development";

const MessageScrollerViewport: FC<IMessageScrollerViewportProps> = ({
    onContentSizeChange: onContentSizeChangeProp,
    onScrollBeginDrag: onScrollBeginDragProp,
    onPointerEnter: onPointerEnterProp,
    onPointerLeave: onPointerLeaveProp,
    onLayout: onLayoutProp,
    onScroll: onScrollProp,
    customTheme,
    children,
    onFocus,
    onBlur,
    style,
    ...props
}) => {
    const context = useMessageScrollerContext();

    const rootContext = useMessageScrollerRootContext();

    const currentTheme = customTheme ?? rootContext?.customTheme;

    const {
        durations,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(rootContext?.customLocalize);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDescendantFocusIgnored: true,
        onFocus,
        onBlur
    });

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isScrolling,
        setIsScrolling
    ] = useState(false);

    const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const {
        container: containerDynamicStyle
    } = useStyles({
        isScrollbarVisible: isHovered || isScrolling,
        isFocusVisible,
        borders,
        colors
    });

    useEffect(() => {
        context?.store.update({
            isFocusVisible
        });
    }, [
        isFocusVisible
    ]);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "MessageScroller.Viewport: it must be rendered inside a MessageScroller.Provider."
        });
    }, [
        context
    ]);

    useEffect(() => {
        return () => {
            clearTimeout(scrollTimerRef.current);
        };
    }, []);

    const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if(Platform.OS === "web") {
            setIsScrolling(true);

            clearTimeout(scrollTimerRef.current);

            scrollTimerRef.current = setTimeout(() => {
                setIsScrolling(false);
            }, durations.hoverOpen);
        }

        const {
            layoutMeasurement,
            contentOffset,
            contentSize
        } = event.nativeEvent;

        context?.engine.handleScroll({
            viewportHeight: layoutMeasurement.height,
            contentHeight: contentSize.height,
            offset: contentOffset.y
        });

        onScrollProp?.(event);
    };

    const onContentSizeChange = (width: number, height: number) => {
        context?.engine.handleContentSizeChange(height);

        onContentSizeChangeProp?.(width, height);
    };

    const onLayout = (event: LayoutChangeEvent) => {
        context?.engine.handleViewportLayout(event.nativeEvent.layout.height);

        onLayoutProp?.(event);
    };

    const onScrollBeginDrag = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        context?.engine.handleFollowRelease();

        onScrollBeginDragProp?.(event);
    };

    const onKeyDown = (event: MessageScrollerViewportKeyboardEventType) => {
        if(event.target !== event.currentTarget || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) {
            return;
        }

        event.preventDefault();

        context?.engine.handleFocusFirstVisible();
    };

    const onWheel = (event: MessageScrollerViewportWheelEventType) => {
        if(event.deltaY < 0) {
            context?.engine.handleFollowRelease();
        }
    };

    const onPointerEnter: typeof onPointerEnterProp = (event) => {
        setIsHovered(true);

        onPointerEnterProp?.(event);
    };

    const onPointerLeave: typeof onPointerLeaveProp = (event) => {
        setIsHovered(false);

        onPointerLeaveProp?.(event);
    };

    const webProps = Platform.OS === "web" ? {
        onPointerEnter,
        onPointerLeave,
        onKeyDown,
        onWheel
    } : {};

    return <ScrollView
        {...props}
        {...webProps}
        aria-label={rootContext?.title ?? localize("messages")}
        contentContainerStyle={stylesheet.content}
        onContentSizeChange={onContentSizeChange}
        onFocus={interactionHandlers.onFocus}
        onScrollBeginDrag={onScrollBeginDrag}
        onBlur={interactionHandlers.onBlur}
        keyboardShouldPersistTaps="handled"
        ref={context?.engine.viewportRef}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        scrollEventThrottle={16}
        onLayout={onLayout}
        onScroll={onScroll}
        role="region"
        tabIndex={0}
    >
        {children}
    </ScrollView>;
};
export default MessageScrollerViewport;
